import React, { useEffect, useRef, useState } from 'react';
import { Center, Heading, Link, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';
import { IS_IOS, IS_NATIVE } from 'clipsal-cortex-utils/src/constants/common-constants';
import { useOnMount } from 'clipsal-cortex-utils/src/hooks/use-on-mount';

import { useReduxDispatch } from '../../../app/store';
import { ArcButton } from '../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { WifiNetwork, WifiNetworkList } from '../../../common/components/WifiNetworkList';
import { WiFiPasswordDialog } from '../../../common/components/WiFiPasswordDialog';
import { useTariffs } from '../../../features/tariff/tariffApi';
import {
  trackChargerAddedSuccessfully,
  trackReceivedNetworkList,
  trackUserOfflineSetupError,
} from '../../../utils/common/analytics';
import { timeout } from '../../../utils/common/common-utils';
import { waitForReconnectGate } from '../../../utils/common/wifi-reconnect-utils';
import {
  trackDynatraceChargerAddedSuccessfully,
  trackDynatraceLocalAuthResult,
  trackDynatraceReceivedNetworkList,
  trackDynatraceUserOfflineSetupError,
  trackDynatraceWifiAppliedResult,
  trackDynatraceWifiRecoveryCompleted,
} from '../../../utils/native/dynatrace-events';
import { selectSite } from '../../site/siteSlice';
import { EVSE_FACTORY_PIN_CODE } from '../constants';
import { selectDeviceSetupState, syncDetectedEvseModel } from '../device-setup/deviceSetupSlice';
import { useSetupBackUrl } from '../device-setup/use-setup-back-url';
import { useDebugLogs } from '../use-debug-logs';
import {
  connectToSelectedNetwork,
  fetchWifiNetworks,
  getAuthToken,
  getWifiSettings,
  isConnectedToWifi,
  isEvseApReachable,
  putWifiSettings,
  WIFI_SWITCH_SETTLE_MS,
} from './wifi-connect-helper';
import { selectWifiConfigState } from './wifiConfigSlice';

type SetupStatus = 'READY' | 'SCANNING' | 'CONNECTING' | 'CONFIRMING';

type State = {
  selectedWifiNetworkIndex: number | null;
  status: SetupStatus;
  wifiNetworks: WifiNetwork[];
  existingWifiSsid: string | null;
};

const INITIAL_STATE: State = {
  selectedWifiNetworkIndex: null,
  status: 'READY',
  wifiNetworks: [],
  existingWifiSsid: null,
};

/* istanbul ignore next -- @preserve */
export const DeviceWifiConnection = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const { t } = useTranslation();
  const { debugToast } = useDebugLogs();
  const { withSetupBackUrl } = useSetupBackUrl();
  const passwordDialog = useDisclosure();
  const configAlertDialog = useDisclosure();
  const { isEditing } = useSelector(selectWifiConfigState);
  const { qrCodeValues } = useSelector(selectDeviceSetupState);
  const site = useSelector(selectSite);
  const [state, setState] = useState(INITIAL_STATE);
  const { selectedWifiNetworkIndex, status, wifiNetworks, existingWifiSsid } = state;
  const { tariffs } = useTariffs();
  const { isOnboarding } = useSelector(selectSite);
  const localAuthAttemptRef = useRef(0);
  const wifiApplyAttemptRef = useRef(0);

  const onboardingTariffPath = `/sites/${site.site_id}/account/tariff/new?direction=forward&backURL=/sites/${site.site_id}/home/device-status?direction=back`;

  useEffect(() => {
    if (!qrCodeValues) {
      navigate('../device-scan');
    }
  }, [qrCodeValues, navigate]);

  async function ensureBearerToken(): Promise<string | null> {
    localAuthAttemptRef.current += 1;
    try {
      const token = await getAuthToken(EVSE_FACTORY_PIN_CODE, toast);
      trackDynatraceLocalAuthResult('success', localAuthAttemptRef.current, isEditing);
      return token;
    } catch (error) {
      debugToast('Failed to get auth token', JSON.stringify(error), 8000);
      trackDynatraceLocalAuthResult('failure', localAuthAttemptRef.current, isEditing);

      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceWifiConnection',
          method: 'ensureBearerToken',
        },
      });
      toast({
        title: t('EVSE.error accessing charger'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
        isClosable: true,
      });
      return null;
    }
  }

  async function reconnectToDeviceWifi(currentSsid?: string, homeSsid?: string, password = ''): Promise<boolean> {
    if (!IS_NATIVE) {
      return true;
    }

    try {
      if (IS_IOS && homeSsid && password) {
        // On iOS, disconnecting from the EVSE AP is unreliable, so actively join the
        // target home network when we have the credentials.
        await connectToSelectedNetwork(currentSsid ?? '', homeSsid, password);
      } else {
        // On Android, disconnect from the EVSE AP and wait for the OS to finish the
        // handoff instead of treating a single early check as a failure.
        await CapacitorWifiConnect.disconnect();
      }

      const canContinue = await waitForReconnectGate(qrCodeValues?.wifi?.ssid);
      if (!canContinue) {
        throw new Error('Device is still offline or connected to the EV Charger Wi-Fi');
      }

      return true;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceWifiConnection',
          method: 'reconnectToDeviceWifi',
        },
      });

      const errorMessage = error instanceof Error ? error.message : 'offline_setup_error';
      trackUserOfflineSetupError();
      trackDynatraceUserOfflineSetupError(errorMessage, isEditing);
      return false;
    }
  }

  async function checkExistingWifiConfig() {
    setState((prevState) => ({
      ...prevState,
      selectedWifiNetworkIndex: null,
      status: 'SCANNING',
      wifiNetworks: [],
    }));

    const token = await ensureBearerToken();
    if (!token) {
      setState((prevState) => ({ ...prevState, status: 'READY' }));
      return;
    }

    try {
      const response = await getWifiSettings(token, toast);
      if (response?.ssid) {
        setState((prevState) => ({ ...prevState, status: 'READY', existingWifiSsid: response.ssid }));
        configAlertDialog.onOpen();
        return;
      }
    } catch (error) {
      debugToast('Error fetching wifi settings after connecting to charger Wi-Fi', JSON.stringify(error));
      setState((prevState) => ({ ...prevState, status: 'READY' }));
    }

    // No existing config found, fetch available networks.
    await fetchLocalWifiNetworks();
  }

  async function fetchLocalWifiNetworks() {
    const token = await ensureBearerToken();
    if (!token) {
      setState((prevState) => ({ ...prevState, status: 'READY' }));
      return;
    }

    setState((prevState) => ({
      ...prevState,
      selectedWifiNetworkIndex: null,
      status: 'SCANNING',
      wifiNetworks: [],
    }));

    try {
      debugToast('Fetching wifi networks', 'Requesting wifi networks from EVSE Charger');
      const wifiNetworks = (await fetchWifiNetworks(token, toast)) as WifiNetwork[];
      trackReceivedNetworkList(wifiNetworks.length);
      trackDynatraceReceivedNetworkList(wifiNetworks.length, isEditing);
      debugToast('Fetched wifi networks', `Found ${wifiNetworks.length} networks`);

      setState((prevState) => ({ ...prevState, status: 'READY', wifiNetworks }));
    } catch (error) {
      debugToast('Error fetching wifi networks', JSON.stringify(error), 8000);
      setState((prevState) => ({ ...prevState, status: 'READY' }));

      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceWifiConnection',
          method: 'fetchWifiNetworks',
        },
      });
      toast({
        title: t('Set Up Hardware.error fetching wifi'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
        isClosable: true,
      });
    }
  }

  useOnMount(() => {
    if (isEditing) {
      fetchLocalWifiNetworks();
    } else {
      checkExistingWifiConfig();
    }
  });

  function handleWifiNetworkClick(index: number | null) {
    // Clicking the same network again unselects it.
    const newIndex = index === selectedWifiNetworkIndex ? null : index;
    setState((prevState) => ({ ...prevState, selectedWifiNetworkIndex: newIndex }));
  }

  function handleWifiConnectionFailure() {
    setState((prevState) => ({
      ...prevState,
      selectedWifiNetworkIndex: null,
      status: 'READY',
    }));
    toast({
      title: t('Set Up Hardware.error connecting to wifi'),
      description: t('Set Up Hardware.check wifi pw'),
      status: 'error',
      isClosable: true,
    });
  }

  function handleSuccessfulSetupNavigation() {
    // The phone is back online here, so the model detected over Soft-AP can now reach the backend.
    dispatch(syncDetectedEvseModel());

    if (isEditing) {
      trackDynatraceWifiRecoveryCompleted();
      navigate('../device-wifi-configuration', { state: { showConnectionStatusLoader: true } });
      return;
    }

    trackChargerAddedSuccessfully();
    trackDynatraceChargerAddedSuccessfully();

    if (isOnboarding && tariffs.length === 0) {
      navigate(onboardingTariffPath);
    } else {
      navigate('../device-status');
    }
  }

  async function handleConfirmExistingWifiConfig() {
    setState((prevState) => ({ ...prevState, status: 'CONFIRMING' }));

    const reconnected = await reconnectToDeviceWifi();

    if (reconnected) {
      configAlertDialog.onClose();
      handleSuccessfulSetupNavigation();
      setState((prevState) => ({ ...prevState, status: 'READY' }));
      return;
    }

    navigate('../no-wifi-connection');
  }

  async function handleConnectToWifiNetwork(password: string) {
    if (selectedWifiNetworkIndex === null) {
      toast({
        title: t('Set Up Hardware.no wifi network selected'),
        description: t('Set Up Hardware.error select your wifi'),
        status: 'error',
        isClosable: true,
      });
      setState((prevState) => ({ ...prevState, status: 'READY' }));
      return;
    }

    let reconnected = false;
    try {
      let currentSsid = '';
      // Check that we're still connected to the EVSE's Wi-Fi network before attempting to put the new Wi-Fi settings.
      if (IS_NATIVE) {
        setState((prevState) => ({ ...prevState, status: 'CONNECTING' }));
        const current = await CapacitorWifiConnect.getDeviceSSID().catch((error) => {
          if (!IS_IOS) throw error;
          return undefined;
        });
        currentSsid = current?.value ?? '';
        const evseSsid = qrCodeValues?.wifi?.ssid;
        const isApReachable = IS_IOS && !currentSsid && (await isEvseApReachable());

        if (currentSsid !== evseSsid && !isApReachable) {
          toast({
            title: t('Set Up Hardware.wifi connection'),
            description: (
              <Text>
                {IS_IOS && !currentSsid
                  ? t('Set Up Hardware.enable precise location for charger wifi')
                  : t('EVSE.device is not connected', { currentSsid, evseSsid })}{' '}
                <Link
                  onClick={() => {
                    toast.closeAll();
                    navigate(withSetupBackUrl('../device-power-up-instructions'));
                  }}
                  textDecoration="underline"
                >
                  {t('Set Up Hardware.go to power up instructions')}
                </Link>
              </Text>
            ),
            status: 'warning',
            duration: 15000,
            isClosable: true,
          });
          setState((prevState) => ({ ...prevState, status: 'READY' }));
          return;
        }
      }
      setState((prevState) => ({ ...prevState, status: 'CONNECTING' }));

      debugToast('Putting wifi settings', `SSID: ${wifiNetworks[selectedWifiNetworkIndex].ssid}`);

      const token = await ensureBearerToken();
      if (!token) {
        setState((prevState) => ({ ...prevState, status: 'READY' }));
        return;
      }

      wifiApplyAttemptRef.current += 1;
      try {
        await putWifiSettings({ ssid: wifiNetworks[selectedWifiNetworkIndex].ssid, password }, token, toast);
      } catch (error) {
        debugToast('Error connecting EVSE to wifi network', JSON.stringify(error), 8000);

        // Only throw if it's NOT a timeout error. Timeouts are expected here since the device may disconnect from the Wi-Fi network while applying settings and rebooting, especially on iOS. We want to proceed with the flow and attempt to connect to the new network in this case, rather than showing an error to the user.
        const isTimeout = error instanceof Error && /timeout|timed out/.test(error.message.toLowerCase());
        if (!isTimeout) {
          throw error;
        }
      }

      // Spec: wait 10 seconds after the PUT to allow the charger to disconnect from
      // its previous network and begin joining the new one before we start polling.
      await timeout(WIFI_SWITCH_SETTLE_MS);

      // After putting the new Wi-Fi settings, we check if the device successfully applied the settings by comparing the current SSID with the expected SSID. If they don't match, it's likely that the device failed to apply the new settings and is still connected to the old network, so we show an error to the user and allow them to try again.
      const isConnected = await isConnectedToWifi(token, toast);
      if (!isConnected) {
        debugToast('EVSE wifi connection error', 'EVSE failed to connect to new wifi network');
        trackDynatraceWifiAppliedResult('failure', wifiApplyAttemptRef.current, isEditing);
        // Back to ready state to allow user to try again, since the device may not have applied the new settings successfully.
        handleWifiConnectionFailure();
        return;
      }

      trackDynatraceWifiAppliedResult('success', wifiApplyAttemptRef.current, isEditing);
      reconnected = await reconnectToDeviceWifi(currentSsid, wifiNetworks[selectedWifiNetworkIndex].ssid, password);
    } catch (error) {
      debugToast('Error connecting to wifi network', JSON.stringify(error), 8000);
      trackDynatraceWifiAppliedResult('failure', wifiApplyAttemptRef.current, isEditing);
      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'DeviceWifiConnection',
          method: 'handleConnectToWifiNetwork',
        },
      });

      setState((prevState) => ({
        ...prevState,
        selectedWifiNetworkIndex: null,
        status: 'READY',
      }));
      navigate('../wifi-setup-fail');
      return;
    }

    if (reconnected) {
      handleSuccessfulSetupNavigation();
      setState((prevState) => ({ ...prevState, status: 'READY' }));
      return;
    }

    navigate('../no-wifi-connection');
  }

  return (
    <SlidingAnimationPageBase
      title={t('Common.device setup')}
      backURL={withSetupBackUrl('../device-wifi-2ghz-info?direction=back')}
      display="flex"
      flexDirection="column"
      // Pull-to-refresh reloads the whole app, wiping the in-memory device setup state (qrCodeValues)
      // while the phone is still connected to the EVSE AP with no internet - stranding the user on the
      // generic InternetConnectionRecoveryPage. Disable it here since there's nothing to recover into.
      disablePullToRefresh
    >
      <Center flexDirection="column" px={5} mt={4} textAlign="center" flex={1}>
        <Heading size={'lg'}>{t('Set Up Hardware.select your wifi')}</Heading>
        <Text mb={4} mt={1}>
          {t('EVSE.choose network')}
        </Text>

        <WifiNetworkList
          wifiNetworks={wifiNetworks}
          selectedIndex={selectedWifiNetworkIndex}
          onClick={handleWifiNetworkClick}
          isLoading={status === 'SCANNING'}
          isDisabled={status === 'CONNECTING'}
        />

        <ArcButton
          data-testid="retry-button"
          hidden={selectedWifiNetworkIndex !== null}
          onClick={fetchLocalWifiNetworks}
          isDisabled={status !== 'READY'}
          mt="auto"
          mb={8}
          w={'100%'}
          arcColor="#3DCD57"
        >
          {t('Set Up Hardware.scan wifi networks')}
        </ArcButton>

        <ArcButton
          data-testid="ev-charger-continue"
          hidden={selectedWifiNetworkIndex === null}
          onClick={passwordDialog.onOpen}
          mt="auto"
          mb={8}
          w={'100%'}
          arcColor="#3DCD57"
          isDisabled={status !== 'READY'}
          isLoading={status === 'CONNECTING'}
        >
          {t('Common.continue')}
        </ArcButton>
      </Center>

      <WiFiPasswordDialog
        ssid={selectedWifiNetworkIndex !== null ? wifiNetworks[selectedWifiNetworkIndex]?.ssid : ''}
        isOpen={passwordDialog.isOpen}
        onClose={passwordDialog.onClose}
        onConfirm={handleConnectToWifiNetwork}
      />
      <AlertDialogModal
        isOpen={configAlertDialog.isOpen}
        onClose={() => undefined}
        header={t('Set Up Hardware.wifi already configured')}
        subHeader={t('Set Up Hardware.wifi already configured text', { ssid: existingWifiSsid ?? '' })}
        confirmButtonName={t('Common.continue')}
        cancelButtonName={t('Set Up Hardware.scan networks')}
        isConfirming={status === 'CONFIRMING'}
        cancelButtonProps={{ isDisabled: status === 'CONFIRMING' }}
        onConfirm={handleConfirmExistingWifiConfig}
        onCancel={async () => {
          configAlertDialog.onClose();
          setState((prevState) => ({ ...prevState, existingWifiSsid: null }));
          await fetchLocalWifiNetworks();
        }}
        dialogContentTestId="device-wifi-configured-dialog"
        cancelButtonTestId="device-wifi-scan-button"
        confirmButtonTestId="device-wifi-continue-button"
      />
    </SlidingAnimationPageBase>
  );
};
